import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { statusColor, formatDate } from '../../utils/format.js'
import {
  listScreeningCampaigns,
  createScreeningCampaign,
  listScreeningProtocols,
  createScreeningProtocol,
  updateScreeningProtocol,
  deleteScreeningProtocol,
  createScreeningProtocolItem,
  deleteScreeningProtocolItem,
} from '../../api/screening.js'
import { listOrganizationsByType } from '../../api/organizations.js'

/**
 * /superuser/screening — Phase 1 admin surface for routine medical
 * screening. Two stacked sections: Campaigns (the screening events) and
 * Protocols (the editable checklist templates). Platform-admin only —
 * gated in App.jsx via RequireRole roles={['admin']}.
 */
export default function ScreeningPage() {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Routine Medical Screening"
          subtitle="Campaigns deliver routine medical checks to a subscribing organisation's workforce. Each person screened is recorded as a clinical encounter; protocols define what the checklist captures."
        />
      </Card>
      <CampaignsSection />
      <ProtocolsSection />
    </div>
  )
}

// ===========================================================================
// Campaigns
// ===========================================================================

function CampaignsSection() {
  const [showNew, setShowNew] = useState(false)
  const campaignsQ = useQuery({
    queryKey: ['screening-campaigns'],
    queryFn: () => listScreeningCampaigns(),
  })
  const campaigns = campaignsQ.data || []

  return (
    <Card>
      <CardHeader
        title="Campaigns"
        subtitle="Each campaign screens one organisation's workforce against a protocol."
        right={
          <button
            onClick={() => setShowNew(true)}
            className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full bg-wantok-600 text-white hover:bg-wantok-700 transition-colors"
          >
            + New campaign
          </button>
        }
      />
      {campaignsQ.error && (
        <ErrorBanner error={campaignsQ.error} onRetry={() => campaignsQ.refetch()} />
      )}
      {campaignsQ.isLoading && (
        <div className="text-sm text-slate-400 py-4">Loading campaigns…</div>
      )}
      {campaignsQ.data && (
        campaigns.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            No campaigns yet. Create the first one with “+ New campaign”.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2">Campaign</th>
                  <th className="text-left p-2">Organisation</th>
                  <th className="text-left p-2">Protocol</th>
                  <th className="text-left p-2 w-28">Status</th>
                  <th className="text-right p-2 w-24">Screened</th>
                  <th className="text-left p-2 w-28">Start</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <tr key={c.id} className="hover:bg-sand-50/50">
                    <td className="p-2">
                      <Link
                        to={`/superuser/screening/campaigns/${c.id}`}
                        className="font-medium text-wantok-700 hover:underline"
                      >
                        {c.name}
                      </Link>
                    </td>
                    <td className="p-2 text-slate-600">{c.organization_name}</td>
                    <td className="p-2 text-slate-600">{c.protocol_name}</td>
                    <td className="p-2">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${statusColor(c.status)}`}>
                        {c.status_display || c.status}
                      </span>
                    </td>
                    <td className="p-2 text-right tabular-nums text-slate-700">
                      {c.screened_count}{c.expected_headcount ? ` / ${c.expected_headcount}` : ''}
                    </td>
                    <td className="p-2 text-slate-600">
                      {c.scheduled_start ? formatDate(c.scheduled_start) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {showNew && <CampaignModal onClose={() => setShowNew(false)} />}
    </Card>
  )
}

function CampaignModal({ onClose }) {
  const qc = useQueryClient()
  const orgsQ = useQuery({
    queryKey: ['screening-orgs'],
    queryFn: () => listOrganizationsByType(),
    staleTime: 5 * 60 * 1000,
  })
  const protocolsQ = useQuery({
    queryKey: ['screening-protocols'],
    queryFn: () => listScreeningProtocols(),
  })
  const orgs = orgsQ.data || []
  const protocols = (protocolsQ.data || []).filter((p) => p.is_active)

  const [form, setForm] = useState({
    name: '',
    organization: '',
    protocol: '',
    delivery_mode: 'site_visit',
    scheduled_start: '',
    scheduled_end: '',
    location: '',
    expected_headcount: '',
  })
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const createMut = useMutation({
    mutationFn: createScreeningCampaign,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-campaigns'] })
      onClose()
    },
  })

  function handleSubmit(e) {
    e.preventDefault()
    createMut.mutate({
      name: form.name,
      organization: parseInt(form.organization, 10),
      protocol: parseInt(form.protocol, 10),
      delivery_mode: form.delivery_mode,
      scheduled_start: form.scheduled_start || null,
      scheduled_end: form.scheduled_end || null,
      location: form.location,
      expected_headcount: form.expected_headcount
        ? parseInt(form.expected_headcount, 10) : 0,
    })
  }

  const ready = form.name.trim() && form.organization && form.protocol

  return (
    <Modal title="New screening campaign" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Field label="Campaign name">
          <input
            type="text" value={form.name} required
            onChange={(e) => setF('name', e.target.value)}
            placeholder="e.g. OPIC Annual Medical — Q2 2026"
            className={inputCls}
          />
        </Field>
        <Field label="Organisation">
          <select value={form.organization} required
            onChange={(e) => setF('organization', e.target.value)}
            className={inputCls}>
            <option value="">Select an organisation…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
        <Field label="Protocol">
          <select value={form.protocol} required
            onChange={(e) => setF('protocol', e.target.value)}
            className={inputCls}>
            <option value="">Select a protocol…</option>
            {protocols.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {protocols.length === 0 && !protocolsQ.isLoading && (
            <div className="text-[11px] text-amber-700 mt-1">
              No active protocols yet — create one in the Protocols section first.
            </div>
          )}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Delivery mode">
            <select value={form.delivery_mode}
              onChange={(e) => setF('delivery_mode', e.target.value)}
              className={inputCls}>
              <option value="site_visit">Site visit</option>
              <option value="clinic">Clinic</option>
              <option value="telehealth">Telehealth</option>
            </select>
          </Field>
          <Field label="Expected headcount">
            <input type="number" min="0" value={form.expected_headcount}
              onChange={(e) => setF('expected_headcount', e.target.value)}
              className={inputCls} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Scheduled start">
            <input type="date" value={form.scheduled_start}
              onChange={(e) => setF('scheduled_start', e.target.value)}
              className={inputCls} />
          </Field>
          <Field label="Scheduled end">
            <input type="date" value={form.scheduled_end}
              onChange={(e) => setF('scheduled_end', e.target.value)}
              className={inputCls} />
          </Field>
        </div>
        <Field label="Location (optional)">
          <input type="text" value={form.location}
            onChange={(e) => setF('location', e.target.value)}
            placeholder="e.g. OPIC head office, Port Moresby"
            className={inputCls} />
        </Field>
        {createMut.error && <FormError error={createMut.error} />}
        <ModalActions onClose={onClose} submitting={createMut.isPending}
          submitLabel="Create campaign" disabled={!ready} />
      </form>
    </Modal>
  )
}

// ===========================================================================
// Protocols
// ===========================================================================

function ProtocolsSection() {
  const [showNew, setShowNew] = useState(false)
  const protocolsQ = useQuery({
    queryKey: ['screening-protocols'],
    queryFn: () => listScreeningProtocols(),
  })
  const protocols = protocolsQ.data || []

  return (
    <Card>
      <CardHeader
        title="Protocols"
        subtitle="Editable checklist templates — what each screening should capture. A protocol can be reused across campaigns."
        right={
          <button onClick={() => setShowNew(true)}
            className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full bg-wantok-600 text-white hover:bg-wantok-700 transition-colors">
            + New protocol
          </button>
        }
      />
      {protocolsQ.error && (
        <ErrorBanner error={protocolsQ.error} onRetry={() => protocolsQ.refetch()} />
      )}
      {protocolsQ.isLoading && (
        <div className="text-sm text-slate-400 py-4">Loading protocols…</div>
      )}
      {protocolsQ.data && (
        protocols.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            No protocols yet. Create one with “+ New protocol”.
          </div>
        ) : (
          <div className="space-y-2">
            {protocols.map((p) => <ProtocolRow key={p.id} protocol={p} />)}
          </div>
        )
      )}
      {showNew && <ProtocolModal onClose={() => setShowNew(false)} />}
    </Card>
  )
}

function ProtocolModal({ onClose }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ name: '', description: '' })
  const createMut = useMutation({
    mutationFn: createScreeningProtocol,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-protocols'] })
      onClose()
    },
  })
  return (
    <Modal title="New screening protocol" onClose={onClose}>
      <form
        onSubmit={(e) => { e.preventDefault(); createMut.mutate(form) }}
        className="space-y-3"
      >
        <Field label="Protocol name">
          <input type="text" required value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Annual Medical, Pre-Employment, Drivers' Medical"
            className={inputCls} />
        </Field>
        <Field label="Description (optional)">
          <textarea rows={3} value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className={inputCls} />
        </Field>
        {createMut.error && <FormError error={createMut.error} />}
        <ModalActions onClose={onClose} submitting={createMut.isPending}
          submitLabel="Create protocol" disabled={!form.name.trim()} />
      </form>
    </Modal>
  )
}

const ITEM_CATEGORIES = [
  ['vital', 'Vital sign'],
  ['measurement', 'Measurement'],
  ['lab_panel', 'Lab panel / test'],
  ['imaging', 'Imaging study'],
  ['question', 'Question / observation'],
]

function ProtocolRow({ protocol }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [newItem, setNewItem] = useState({ category: 'vital', key: '', label: '' })

  const addItemMut = useMutation({
    mutationFn: createScreeningProtocolItem,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-protocols'] })
      setNewItem({ category: 'vital', key: '', label: '' })
    },
  })
  const delItemMut = useMutation({
    mutationFn: deleteScreeningProtocolItem,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screening-protocols'] }),
  })
  const [editing, setEditing] = useState(false)
  const deleteMut = useMutation({
    mutationFn: deleteScreeningProtocol,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screening-protocols'] }),
  })

  const items = protocol.items || []
  const canAdd = newItem.key.trim() && newItem.label.trim()

  return (
    <div className="border border-sand-200 rounded-lg">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-sand-50/50"
      >
        <div>
          <span className="font-medium text-slate-800">{protocol.name}</span>
          {!protocol.is_active && (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">inactive</span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {protocol.item_count} item{protocol.item_count === 1 ? '' : 's'}
          {' · '}{protocol.campaign_count} campaign{protocol.campaign_count === 1 ? '' : 's'}
          <span className="ml-2 text-slate-400">{open ? '▲' : '▼'}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-sand-200 px-3 py-3 space-y-3">
          {protocol.description && (
            <p className="text-xs text-slate-500">{protocol.description}</p>
          )}
          {items.length > 0 ? (
            <table className="w-full text-xs">
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="py-1 pr-2 w-28 text-slate-400 uppercase tracking-wide text-[10px]">
                      {it.category}
                    </td>
                    <td className="py-1 pr-2 font-mono text-slate-500">{it.key}</td>
                    <td className="py-1 pr-2 text-slate-700">{it.label}</td>
                    <td className="py-1 pr-2 text-slate-400">
                      {it.is_required ? 'required' : 'optional'}
                    </td>
                    <td className="py-1 text-right">
                      <button
                        onClick={() => delItemMut.mutate(it.id)}
                        className="text-rose-600 hover:underline"
                      >
                        remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-slate-400 italic">No checklist items yet.</p>
          )}
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Category</div>
              <select value={newItem.category}
                onChange={(e) => setNewItem((n) => ({ ...n, category: e.target.value }))}
                className="border border-sand-300 rounded-md px-2 py-1 text-xs bg-white">
                {ITEM_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Key</div>
              <input type="text" value={newItem.key}
                onChange={(e) => setNewItem((n) => ({ ...n, key: e.target.value }))}
                placeholder="bp"
                className="border border-sand-300 rounded-md px-2 py-1 text-xs w-24" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Label</div>
              <input type="text" value={newItem.label}
                onChange={(e) => setNewItem((n) => ({ ...n, label: e.target.value }))}
                placeholder="Blood pressure"
                className="border border-sand-300 rounded-md px-2 py-1 text-xs w-full" />
            </div>
            <button
              onClick={() => {
                if (!canAdd) return
                addItemMut.mutate({
                  protocol: protocol.id,
                  category: newItem.category,
                  key: newItem.key.trim(),
                  label: newItem.label.trim(),
                  is_required: true,
                  sort_order: items.length + 1,
                })
              }}
              disabled={addItemMut.isPending || !canAdd}
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-wantok-600 text-white hover:bg-wantok-700 disabled:opacity-50"
            >
              {addItemMut.isPending ? 'Adding…' : 'Add item'}
            </button>
          </div>
          {addItemMut.error && <FormError error={addItemMut.error} />}
          <div className="flex items-center justify-between pt-2 border-t border-sand-100">
            <button type="button" onClick={() => setEditing(true)}
              className="text-xs text-wantok-700 hover:underline">
              Edit protocol
            </button>
            {protocol.campaign_count === 0 ? (
              <button type="button"
                onClick={() => {
                  if (window.confirm(`Delete protocol “${protocol.name}”?`)) {
                    deleteMut.mutate(protocol.id)
                  }
                }}
                className="text-xs text-rose-600 hover:underline">
                Delete protocol
              </button>
            ) : (
              <span className="text-[11px] text-slate-400">
                In use by {protocol.campaign_count} campaign{protocol.campaign_count === 1 ? '' : 's'} — can't delete
              </span>
            )}
          </div>
        </div>
      )}
      {editing && (
        <ProtocolEditModal protocol={protocol} onClose={() => setEditing(false)} />
      )}
    </div>
  )
}

function ProtocolEditModal({ protocol, onClose }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    name: protocol.name || '',
    description: protocol.description || '',
    is_active: protocol.is_active,
  })
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const mut = useMutation({
    mutationFn: () => updateScreeningProtocol(protocol.id, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-protocols'] })
      onClose()
    },
  })
  return (
    <Modal title="Edit protocol" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); mut.mutate() }} className="space-y-3">
        <Field label="Protocol name">
          <input type="text" required value={form.name}
            onChange={(e) => setF('name', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Description">
          <textarea rows={3} value={form.description}
            onChange={(e) => setF('description', e.target.value)} className={inputCls} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.is_active}
            onChange={(e) => setF('is_active', e.target.checked)} />
          Active — available for new campaigns
        </label>
        {mut.error && <FormError error={mut.error} />}
        <ModalActions onClose={onClose} submitting={mut.isPending}
          submitLabel="Save protocol" disabled={!form.name.trim()} />
      </form>
    </Modal>
  )
}

// ===========================================================================
// Shared building blocks
// ===========================================================================

const inputCls = 'w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white'

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  )
}

function FormError({ error }) {
  const msg = error?.response?.data?.error?.message
    || error?.response?.data?.detail
    || error?.message
    || 'Something went wrong.'
  return (
    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
      {String(msg)}
    </div>
  )
}

function ModalActions({ onClose, submitting, submitLabel, disabled }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} disabled={submitting}
        className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50">
        Cancel
      </button>
      <button type="submit" disabled={submitting || disabled}
        className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-50">
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  )
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-sand-200 w-full max-w-lg mt-12 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
            aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  )
}
